import { Cart, CartLineNode, CartAttribute } from '@/lib/shopify';
import { getCartLineItemProperty } from '@/utils/shopify';

export function shouldUsePlayGeniusCheckout(cart: Cart | null): boolean {
  if (!cart?.lines?.edges?.length) {
    return false;
  }

  for (const edge of cart.lines.edges) {
    const node = edge.node;

    if (shouldLineItemUsePlayGeniusCheckout(node)) {
      return true;
    }
  }

  return false;
}

const PRODUCT_HANDLES = [
  'subscription-gifting',
  'reading-skill-set',
  'the-bath-set',
];

const PROPERTIES_MUST_BE_TRUE = ['is_playgenius_subscription'];

const PROPERTIES_ANY_VALUE = [
  'is_book_bundle',
  'price_source',
  'subscription_plan_settings',
];

const SOURCES = ['sub_shop'];

const COURSE_PROPERTIES = [
  'is_course_pack',
  'is_skill_set',
  '_is_course_pack',
  '_is_skill_set',
];

function getCartProperty(
  name: string,
  attributes: any,
): string | null | undefined {
  return (
    getCartLineItemProperty(name, attributes) ??
    getCartLineItemProperty(`_${name}`, attributes)
  );
}

function shouldLineItemUsePlayGeniusCheckout(node: CartLineNode): boolean {
  const productHandle = node.merchandise?.product?.handle;

  if (productHandle && PRODUCT_HANDLES.includes(productHandle)) {
    return true;
  }

  for (const property of PROPERTIES_MUST_BE_TRUE) {
    if (getCartProperty(property, node.attributes) === 'true') {
      return true;
    }
  }

  for (const property of PROPERTIES_ANY_VALUE) {
    if (getCartProperty(property, node.attributes)) {
      return true;
    }
  }

  const source = getCartProperty('source', node.attributes);
  if (source && SOURCES.includes(source)) {
    return true;
  }

  for (const property of COURSE_PROPERTIES) {
    if (getCartProperty(property, node.attributes) === 'true') {
      return true;
    }
  }

  return false;
}

/**
 * Utilities for converting cart properties for Shopify checkout
 */

export function isPropertyHidden(property: string): boolean {
  const firstCharacter = property?.[0];
  return firstCharacter === '_';
}

export function convertPublicPropertiesToPrivateProperties(
  attributes: CartAttribute[],
): CartAttribute[] {
  return attributes.map((attribute) => {
    if (isPropertyHidden(attribute.key)) {
      // Already hidden, return as is
      return attribute;
    } else {
      // Convert to hidden by adding underscore prefix
      return {
        key: '_' + attribute.key,
        value: attribute.value,
      };
    }
  });
}

export function shouldConvertCartItemsToHidden(cart: Cart | null): boolean {
  if (!cart?.lines?.edges?.length) return false;

  // Check if all items have been converted to hidden properties
  const allItemsHaveBeenConverted = cart.lines.edges.every((edge) =>
    edge.node.attributes.every((attr) => isPropertyHidden(attr.key)),
  );

  return !allItemsHaveBeenConverted;
}

/**
 * Determines if a cart is eligible for the PlayGenius checkout A/B test.
 *
 * Eligibility Rules:
 * - Carts with ONLY standalone products → ELIGIBLE
 * - Carts with excluded products (subscriptions, etc) → NOT eligible
 * - Mixed carts (excluded + standalone) → NOT eligible
 *
 * @param cart - Shopping cart
 * @returns true if cart is eligible for the A/B test
 */
export function isCartEligibleForCheckoutTest(cart: Cart | null): boolean {
  if (!cart?.lines?.edges?.length) {
    return false;
  }

  let hasExcludedItems = false;
  let hasStandaloneItems = false;

  // Categorize each item in the cart
  for (const edge of cart.lines.edges) {
    const node = edge.node;
    // If item already uses PlayGenius checkout, it's excluded from the experiment
    if (shouldLineItemUsePlayGeniusCheckout(node)) {
      hasExcludedItems = true;
    } else {
      hasStandaloneItems = true;
    }
  }

  // Only carts with ONLY standalone items are eligible
  return hasStandaloneItems && !hasExcludedItems;
}
